<template>
  <div>
    <div class="authentication-modal-content">
      <VueText sizeLevel="7" weightLevel="5" color="black-100" style="margin-top: 20px;">
        {{ title }}
      </VueText>
      <VueText
        style="border: 1px solid rgba(0, 0, 0, 0.1); border-radius: 10px; margin: 15px 0; line-height: 20px; padding: 16px;"
      >
        {{ description }}
      </VueText>
      <div v-if="timerLabel" class="otp-timer" :class="{ 'otp-timer--warning': secondsLeft < 60 }">
        Kodun geçerlilik süresi: <strong>{{ timerLabel }}</strong>
      </div>
      <div v-else-if="isExpired" class="otp-timer otp-timer--expired">
        Kodun süresi doldu. Lütfen yeni kod isteyin.
      </div>
      <div class="verification-input-wrapper">
        <OtpInput v-model="verificationCode" :has-error="!!codeError" :autofocus="true" />
        <div v-if="codeError" class="verification-error">{{ codeError }}</div>
      </div>
    </div>

    <div v-if="isBanned" style="margin-bottom: 10px;">
      <BrandButton @click="handleResendClick">
        <VueText>Kodu Yeniden Gönder</VueText>
      </BrandButton>
    </div>

    <div class="authentication-modal-footer">
      <BrandButton @click="handleVerifyClick" :disabled="isBanned">
        <VueText>{{ buttonLabel }}</VueText>
      </BrandButton>
    </div>
  </div>
</template>

<script>
import VueText from '@/components/shared/VueText/VueText.vue';
import BrandButton from '@/components/brand/BrandButton/BrandButton';
import OtpInput from './OtpInput.vue';
import mfa from '@/services/Api/mfa';

export default {
  name: 'MultiFactorAuthenticationVerifyPopup',
  components: { VueText, BrandButton, OtpInput },
  data() {
    return {
      verificationCode: '',
      codeError: '',
      secondsLeft: null,
      timerInterval: null,
      isBanned: false,
      unlockDate: null,
      remainingAttempts: 3,
      localExpiresAt: this.expireDate,
    };
  },
  props: {
    mode: {
      type: String,
      default: 'activate',
    },
    selectedPhone: {
      type: Object,
      default: () => ({ phone: '****' }),
    },
    expireDate: {
      type: String,
      default: null,
    },
  },
  emits: ['close', 'verify'],
  computed: {
    title() {
      return this.mode === 'deactivate'
        ? 'İki Aşamalı Giriş Kapatma'
        : 'İki Aşamalı Giriş Aktifleştirme';
    },
    description() {
      return `Sonu ${this.maskedPhone} biten telefon numarasına gelen 6 haneli doğrulama kodunu giriniz.`;
    },
    buttonLabel() {
      return this.mode === 'deactivate' ? 'Kapat' : 'Doğrula';
    },
    timerLabel() {
      if (!this.secondsLeft || this.secondsLeft <= 0) return null;
      const m = Math.floor(this.secondsLeft / 60)
        .toString()
        .padStart(2, '0');
      const s = (this.secondsLeft % 60).toString().padStart(2, '0');
      return `${m}:${s}`;
    },
    isExpired() {
      return this.localExpiresAt !== null && this.secondsLeft <= 0;
    },
    banLabel() {
      if (!this.unlockDate) return '';
      const diff = Math.floor((new Date(this.unlockDate) - Date.now()) / 1000 / 60);
      return `${diff} dakika sonra tekrar deneyebilirsiniz.`;
    },
    isInputDisabled() {
      return this.isExpired || this.isBanned;
    },
    maskedPhone() {
      const phone = this.selectedPhone?.phone;
      if (!phone) return '****';
      const digits = phone.replace(/\D/g, '');
      const last4 = digits.slice(-4);
      const masked = '*'.repeat(digits.length - 4) + last4;
      return masked;
    },
  },
  methods: {
    handleVerifyClick() {
      if (this.verificationCode.length !== 6) {
        this.codeError = 'Lütfen 6 haneli kodu giriniz.';
        return;
      }
      this.codeError = '';
      const activate = this.mode === 'activate';
      mfa
        .postChangeStatus({ activate, code: this.verificationCode })
        .then(response => {
          const { success, isBlocked, remainingAttempts, message } = response.data.Data;

          if (isBlocked) {
            this.isBanned = true;
            this.codeError = message;
            return;
          }

          if (!success) {
            this.remainingAttempts = remainingAttempts;
            this.codeError =
              remainingAttempts > 0
                ? `${message} ${remainingAttempts} deneme hakkınız kaldı.`
                : message;
            return;
          }

          this.$emit('verify', this.verificationCode);
        })
        .catch(error => {
          console.error('postChangeStatus failed:', error);
          this.codeError = 'Doğrulama başarısız. Lütfen tekrar deneyin.';
        });
    },
    handleResendClick() {
      mfa
        .postResendCode()
        .then(response => {
          const { expireDate, isBanned, unlockDate, message } = response.data.Data;

          if (isBanned) {
            this.isBanned = true;
            this.unlockDate = unlockDate;
            this.codeError = message;
            return;
          }

          this.isBanned = false;
          this.remainingAttempts = 3;
          this.verificationCode = '';
          this.codeError = '';
          this.localExpiresAt = expireDate;
          this.startTimer();
        })
        .catch(error => {
          console.error('Resend code failed:', error);
          this.codeError = 'Kod gönderilemedi. Lütfen tekrar deneyin.';
        });
    },
    startTimer() {
      if (!this.localExpiresAt) return;
      clearInterval(this.timerInterval);

      const expireTime = new Date(this.localExpiresAt).getTime();
      const calculate = () => {
        const diff = Math.floor((expireTime - Date.now()) / 1000);
        this.secondsLeft = Math.max(0, diff);
      };

      calculate();
      this.timerInterval = setInterval(() => {
        calculate();
        if (this.secondsLeft === 0) clearInterval(this.timerInterval);
      }, 1000);
    },
  },
  mounted() {
    this.startTimer();
  },

  beforeDestroy() {
    clearInterval(this.timerInterval);
  },
  watch: {
    verificationCode(val) {
      if (val.length === 6 && this.codeError) {
        this.codeError = '';
      }
    },
  },
};
</script>

<style scoped lang="scss">
.authentication-modal-content {
  width: 100%;
  margin-top: 60px;
}

.verification-input-wrapper {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.verification-error {
  margin-top: 10px;
  color: #d32f2f;
  font-size: 13px;
  align-self: center;
}

.otp-timer {
  margin-top: 12px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.5);
  padding: 0 3px;

  &--warning {
    color: #e65100;
  }

  &--expired {
    color: #d32f2f;
  }
}
.authentication-modal-footer {
  width: 100%;
  margin-top: 16px;
}
</style>
